import styled, { keyframes } from 'styled-components';

/**
 * The surfaces an attachment can take.
 *
 * Media is never wrapped in a bubble: a photo, a clip or a voice note IS the
 * message, and a coloured box around it only adds a margin - Telegram's model.
 * The bubble comes back only when the message also carries text, and then it is
 * the picture itself that extends downwards to hold the caption (see
 * `MediaGroup` / `MediaCaption` in MessageBubble.styled).
 *
 * The filename/size footer went with it. It said nothing about a photo you can
 * already see, and it is what made every image read as a file card.
 */

const shimmer = keyframes`
  from { transform: translateX(-160%) skewX(-20deg); }
  to { transform: translateX(260%) skewX(-20deg); }
`;

/**
 * Stands in for a picture that has not arrived yet.
 *
 * Not decoration - it is load-bearing. An `<img>` with no dimensions occupies
 * no space until it decodes, and in a captioned message the caption takes its
 * width FROM the picture (see `spansTheMedia`): with the picture at zero, the
 * bubble collapsed to a sliver and the caption wrapped one letter per line
 * until the image landed. Reserving the box keeps the message the right shape
 * from the moment it is sent.
 *
 * The sweep is the menu's focus glare, looped rather than one-shot, and in grey
 * rather than red - this is a wait, not an event.
 */
export const MediaSkeleton = styled.span`
  position: absolute;
  inset: 0;
  overflow: hidden;
  background: ${({ theme }) => theme.gray};
  pointer-events: none;

  ::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 50%;
    background: linear-gradient(
      90deg,
      transparent,
      ${({ theme }) => theme.white}CC,
      transparent
    );
    transform: translateX(-160%) skewX(-20deg);
    animation: ${shimmer} 1.4s ease-in-out infinite;
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

/**
 * Sits on a picture that is in the conversation but not yet on the server.
 *
 * The message is posted optimistically with the local file, so the photo is
 * there in full the moment Send is pressed - which leaves nothing to say it is
 * still uploading. Telegram answers that with a ring over the picture, and so
 * does this: the same scrim the timestamp chip wears, so the two read as one
 * layer, with the ring in the middle of it.
 *
 * Distinct from `MediaSkeleton` above, which stands in for a picture that is
 * not on screen at all. Here the picture IS on screen; only the upload is
 * outstanding.
 */
export const MediaLoader = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
  pointer-events: none;

  ::after {
    content: '';
    width: 32px;
    height: 32px;
    border-radius: 50%;
    /* Colour on the shorthand, then one edge overridden - the reverse order
       would reset it back to currentColor. */
    border: 3px solid ${({ theme }) => `${theme.white}59`};
    border-top-color: ${({ theme }) => theme.white};
    animation: ${spin} 0.8s linear infinite;
  }
`;

export const MediaFrame = styled.div<{
  $flush?: boolean;
  $clickable?: boolean;
}>`
  /* Anchors MediaTimeChip. */
  position: relative;
  line-height: 0;
  overflow: hidden;
  /* Flush inside a captioned group: the group rounds the top corners and the
     caption strip closes the bottom, so a radius here would cut a notch - and
     the group is the one carrying the shadow in that case. */
  border-radius: ${({ $flush }) => ($flush ? '0' : '12px')};
  /* The same lift a bubble gets, so a photo sits on the thread rather than
     being cut out of it. */
  box-shadow: ${({ theme, $flush }) =>
    $flush ? 'none' : theme.shadows.shadow1};
  ${({ $clickable }) => $clickable && 'cursor: pointer;'}

  /* On a phone the column is the whole screen, and a picture or a clip that
     touches both walls says nothing about who sent it. Same cap as the album. */
  @media (max-width: 800px) {
    max-width: 72vw;
  }

  img {
    display: block;
    /* Centred rather than stretched: a long caption can make the group wider
       than the picture, and blowing a small photo up to match reads worse than
       letting it sit in the middle. */
    margin: 0 auto;
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 300px;
  }
`;

/**
 * Several pictures in one message read as one album rather than a column of
 * separate photos - two per row, hairline gaps, the whole block rounded once.
 *
 * A definite width is what makes the tiles sizeable at all: the bubble around
 * it is shrink-to-fit, so a percentage would have nothing to resolve against.
 */
export const MediaAlbum = styled.div<{ $flush?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2px;
  /*
   * Definite lengths only - no percentage. A width holding one counts as auto
   * while the bubble works out how wide it wants to be, so the tiles offered up
   * their photos' full pixel widths instead and stretched the whole message to
   * the 70% cap, taking the hover actions out into the middle of the thread.
   *
   * The viewport term is what keeps a phone honest: 320px there is the whole
   * column, and an album that touches both walls says nothing about who sent
   * it. vw is safe here where a percentage is not - it does not depend on the
   * parent, so it cannot feed back into the parent's own width.
   */
  width: min(320px, 72vw);
  max-width: 100%;
  border-radius: ${({ $flush }) => ($flush ? '0' : '12px')};
  box-shadow: ${({ theme, $flush }) =>
    $flush ? 'none' : theme.shadows.shadow1};
  overflow: hidden;
`;

/**
 * `$wide` is the odd one out: with an odd number of pictures the first spans
 * the row, so the album never ends on a half-empty line.
 */
export const AlbumTile = styled.div<{ $wide?: boolean }>`
  position: relative;
  grid-column: ${({ $wide }) => ($wide ? 'span 2' : 'auto')};
  aspect-ratio: ${({ $wide }) => ($wide ? '16 / 9' : '1')};
  overflow: hidden;
  cursor: pointer;
  line-height: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

/**
 * Voice notes and files keep the white pill on both sides. The player's own
 * controls are drawn in dark grey and its track in white, so the incoming
 * side's dark surface would swallow them - and alignment plus the name under
 * the message already say whose it is.
 */
export const AudioPill = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  /* 6px on the left, the same as above and below the play button, so it sits in
     the corner of the pill rather than adrift in it. The right keeps its 14px:
     that side is text, not a circle. */
  padding: 6px 12px 6px 6px;
  border-radius: 24px;
  background: ${({ theme }) => theme.white};
  border: ${({ theme }) => theme.borders.border1};
  box-shadow: ${({ theme }) => theme.shadows.shadow1};
  min-width: min(280px, 100%);
  max-width: 340px;

  /* The player fills the pill; the timestamp keeps its natural width. */
  > *:first-child {
    flex: 1;
    min-width: 0;
  }
`;

export const FileChip = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 14px;
  background: ${({ theme }) => theme.white};
  border: ${({ theme }) => theme.borders.border1};
  box-shadow: ${({ theme }) => theme.shadows.shadow1};
  max-width: 340px;
  text-decoration: none;
  color: ${({ theme }) => theme.darkGray};
  font-size: 13px;

  svg {
    flex: none;
  }
`;

export const FileName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const FileSize = styled.span`
  flex: none;
  font-size: 12px;
  color: ${({ theme }) => theme.lightGray};
`;
